import { ZuruflexApiError } from '@zuruflex/api-client';
import type { Vehicle } from '@zuruflex/types';
import {
  Alert,
  Button,
  Combobox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  type ComboboxOption,
} from '@zuruflex/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';

/**
 * Hands a car to another partner.
 *
 * The branch is asked for only when it has to change: a car at a Zuruflex
 * branch stays there, one at the old owner's own pick-up point cannot. Only
 * branches in the car's own currency are offered, because its prices are
 * numbers in that currency and moving it across would restate 300 cedis as
 * 300 francs.
 */
export function ChangeOwnerDialog({ vehicle, onClose }: { vehicle: Vehicle; onClose: () => void }) {
  const { t } = useTranslation(['admin', 'common']);
  const queryClient = useQueryClient();
  const [partnerId, setPartnerId] = useState('');
  const [locationId, setLocationId] = useState('');

  const partners = useQuery({
    queryKey: ['partners', 'all'],
    queryFn: () => api.listPartners({ perPage: 100 }),
  });
  const locations = useQuery({ queryKey: ['locations'], queryFn: () => api.listLocations() });

  const partnerOptions = useMemo<ComboboxOption[]>(
    () =>
      (partners.data?.items ?? [])
        .filter((partner) => partner.id !== vehicle.partnerId)
        .map((partner) => ({ value: partner.id, label: partner.displayName })),
    [partners.data, vehicle.partnerId],
  );

  const usable = useMemo(
    () =>
      (locations.data ?? []).filter(
        (location) =>
          location.currency === vehicle.currency &&
          (location.partnerId === null || location.partnerId === partnerId),
      ),
    [locations.data, vehicle.currency, partnerId],
  );
  const locationOptions = useMemo<ComboboxOption[]>(
    () =>
      usable.map((location) => ({
        value: location.id,
        label: location.name,
        description: `${location.city}, ${location.country}`,
      })),
    [usable],
  );

  // Where it is now, while the chosen partner may still use it.
  const staysPut = usable.some((location) => location.id === vehicle.locationId);
  const branch = locationId || (staysPut ? vehicle.locationId : '');

  const transfer = useMutation({
    mutationFn: () =>
      api.transferVehicle(vehicle.id, {
        partnerId,
        ...(branch !== vehicle.locationId ? { locationId: branch } : {}),
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['vehicle'] }),
        queryClient.invalidateQueries({ queryKey: ['vehicle-paperwork'] }),
        queryClient.invalidateQueries({ queryKey: ['fleet'] }),
      ]);
      onClose();
    },
  });

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('vehicle.changeOwner')}</DialogTitle>
          <DialogDescription>{t('vehicle.changeOwnerHint')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <FormField label={t('vehicle.newOwner')} required>
            {(field) => (
              <Combobox
                id={field.id}
                options={partnerOptions}
                value={partnerId}
                onChange={(value) => {
                  setPartnerId(value);
                  setLocationId('');
                }}
                placeholder={t('vehicle.choosePartner')}
                searchPlaceholder={t('vehicle.search')}
              />
            )}
          </FormField>

          <FormField
            label={t('vehicle.branch')}
            required
            hint={partnerId && !staysPut ? t('vehicle.branchMustChange') : undefined}
          >
            {(field) => (
              <Combobox
                id={field.id}
                options={locationOptions}
                value={branch}
                onChange={setLocationId}
                disabled={!partnerId}
                placeholder={t('vehicle.chooseBranch')}
                searchPlaceholder={t('vehicle.search')}
              />
            )}
          </FormField>

          <Alert variant="info">{t('vehicle.transferNote')}</Alert>

          {transfer.error ? (
            <Alert variant="danger">
              {transfer.error instanceof ZuruflexApiError
                ? transfer.error.message
                : t('common:error.unexpected')}
            </Alert>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common:action.cancel')}
          </Button>
          <Button
            disabled={!partnerId || !branch}
            isLoading={transfer.isPending}
            onClick={() => transfer.mutate()}
          >
            {t('vehicle.transfer')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
